import { useState } from 'react';
import { getPrimarySeries, type Perf10kPoint, type Perf10kResponse } from '../api/chartDataTypes';
import {
    buildSummarySentence,
    formatReportDate,
    reportTypeLabel,
} from '../utils/perf10kFormat';
import { ChartDataSectionShell } from './ChartDataSectionShell';
import { DataPointSelector } from './DataPointSelector';

type Perf10kChartPanelProps = {
    response: Perf10kResponse;
};

/**
 * Perf10k-specific orchestrator. Owns the date-selection state that drives
 * the summary sentence, computes the header strings from the perf10k response,
 * and hands everything off to the shared ChartDataSection shell.
 *
 * The shell takes care of layout, the live region, and announcement re-fires —
 * this component only deals with perf10k semantics.
 */
export function Perf10kChartPanel({ response }: Perf10kChartPanelProps) {
    const { security, title, report_date, data } = response;
    const primarySeries = getPrimarySeries(response);

    // When the user picks a date in the selector, override the banner's end
    // date + end value. A null selection (year/month change, or before any
    // date is picked) falls back to the API's full-range summary.
    const [selectedPoint, setSelectedPoint] = useState<Perf10kPoint | null>(null);

    const headerTitle = `${security.name} (${security.ticker}) — ${title}`;
    const headerSubtitle = `${reportTypeLabel(security.report_type)} · Report date: ${formatReportDate(report_date)}`;
    const summarySentence = buildSummarySentence(
        data.summary,
        security.report_type,
        response.currency,
        selectedPoint ? { date: selectedPoint.date, value: selectedPoint.value } : undefined,
    );

    return (
        <ChartDataSectionShell title={headerTitle} subtitle={headerSubtitle} summary={summarySentence}>
            {primarySeries ? (
                <DataPointSelector<Perf10kPoint>
                    points={primarySeries.points}
                    idPrefix='perf10k'
                    onSelectedPointChange={setSelectedPoint}
                />
            ) : null}
        </ChartDataSectionShell>
    );
}
